import useAnimatedPress from '@ui/animated/useAnimatedPress'
import Gradient from '@ui/effects/Gradient'
import { colors } from '@ui/colors'
import { CheckboxIcon } from '@ui/forms/Checkbox'
import { RadioIcon } from '@ui/forms/Radio'
import Typography from '@ui/typography'
import { useCallback, useEffect, useMemo } from 'react'
import { Image } from 'expo-image'
import { Pressable, View } from 'react-native'
import Animated, {
  interpolate,
  interpolateColor,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated'
import cn from '@ui/utils/cn'

export type CheckboxGroupOption = {
  id: string | number
  title: string
  disabled?: boolean
  children?: React.ReactNode
  desc?: string
}

export type OrderCheckboxItemProps = {
  active: string[]
  last: boolean
  mode: 'checkbox' | 'radio'
  onPress: (id: string) => void
  error?: boolean
}

const height = 72

const getAnimationValue = (
  error: boolean | undefined,
  disabled: boolean | undefined
) => {
  if (error) return -1
  if (disabled) return 0
  return 1
}

const CheckboxGroupItem = ({
  active = [],
  last,
  mode = 'checkbox',
  onPress,
  id,
  title,
  desc,
  disabled,
  children,
  error,
}: Partial<OrderCheckboxItemProps> & CheckboxGroupOption) => {
  const checked = useSharedValue(0)
  const {
    animation: animationPress,
    onPressIn,
    onPressOut,
  } = useAnimatedPress()

  const animateState = useSharedValue(getAnimationValue(error, disabled))

  const isActive = useMemo(() => {
    return active.includes(id.toString())
  }, [active, id])

  const errorAnimation = useSharedValue(0)
  const disabledAnimation = useSharedValue(0)

  const opacityGradient = useAnimatedStyle(() => ({
    opacity: interpolate(checked.value * animateState.value, [0, 1], [0, 1]),
  }))

  const opacityError = useAnimatedStyle(() => ({
    opacity: interpolate(animateState.value, [-1, 0, 1], [1, 0, 0]),
  }))

  const styleCheckbox = useAnimatedStyle(() => ({
    opacity: interpolate(animateState.value, [-1, 0, 1], [1, 0.25, 1]),
  }))

  const styleText = useAnimatedStyle(() => ({
    color: interpolateColor(
      animateState.value,
      [-1, 0, 1],
      [colors.red[600], colors.neutral[300], colors.text]
    ),
  }))

  const styleDesc = useAnimatedStyle(() => ({
    opacity: interpolate(animateState.value, [-1, 0, 1], [0.25, 1, 0.25]),
  }))

  const handlePress = useCallback(() => {
    onPress?.(id.toString())
  }, [onPress, id])

  useEffect(() => {
    checked.value = withSpring(isActive ? 1 : 0)
  }, [isActive])

  useEffect(() => {
    errorAnimation.set(withSpring(error ? 1 : 0))
    disabledAnimation.set(withSpring(disabled ? 1 : 0))
    animateState.set(withSpring(getAnimationValue(error, disabled)))
  }, [error, disabled])

  return (
    <Pressable
      disabled={disabled}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      className={cn(
        'relative w-full flex-row items-center justify-between gap-8 bg-white p-3 pl-8 pr-8',
        !last && 'border-b border-black/10',
        error && 'border-red-600/25'
      )}
      onPress={handlePress}
      style={{ height }}
    >
      <Gradient
        start={[0, 0.5]}
        end={[1, 0.5]}
        colors={[colors.gray[75], colors.gray[0], colors.white]}
        style={[{ height: height - 1 }, opacityGradient]}
      />
      <Gradient
        start={[0, 0.5]}
        end={[1, 0.5]}
        colors={[colors.red[50], colors.red[50], colors.white]}
        style={[{ height: height - 1 }, opacityError]}
      />

      <View className="min-w-40 flex-row items-center gap-8">
        <Animated.View style={styleCheckbox}>
          {mode === 'checkbox' && (
            <CheckboxIcon
              checked={checked}
              colorActive={colors.text}
              error={errorAnimation}
            />
          )}
          {mode === 'radio' && (
            <RadioIcon
              error={errorAnimation}
              checked={checked}
              colorActive={colors.text}
            />
          )}
        </Animated.View>
        <Typography style={styleText} variant="SMB">
          {title}
        </Typography>
      </View>
      <View className="flex-row items-center justify-between">
        <View className="flex-row items-center gap-4">
          {!!desc && (
            <Typography variant="SMB" style={[styleDesc, styleText]}>
              {desc}
            </Typography>
          )}
          {children}
        </View>
      </View>
    </Pressable>
  )
}

export const GroupItemImage = ({
  image,
  disabled,
  ...props
}: CheckboxGroupOption & { image?: string }) => (
  <CheckboxGroupItem {...props} disabled={disabled}>
    {image && (
      <Image
        className={cn(disabled && 'opacity-25')}
        source={{ uri: image }}
        contentFit="contain"
        style={{ width: 120, height: 48 }}
      />
    )}
  </CheckboxGroupItem>
)

export default CheckboxGroupItem
